import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Icon} from '@vio/atlas/Icon'
import {PopUp} from '@vio/atlas/PopUp'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {Price} from '../../search/Price'

export interface Props {
  isOpen: boolean
  taxes: number
  hotelFeesTotal: number
  currencyCode: string
  onClose: () => void
}

interface ContentProps {
  taxes: Props['taxes']
  currencyCode: Props['currencyCode']
  hotelTaxes: number
  withoutPaddings?: boolean
}

interface TaxSectionProps {
  taxValue: number
  sectionTitle: JSX.Element
  sectionSubtitle: JSX.Element
  currencyCode: Props['currencyCode']
}

const NUMBER_OF_DECIMALS_FOR_PRICE = 2

const ContentContainer = styled(Grid)`
  padding: ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500};
  &.--no-paddings {
    padding: 0;
  }
`

const StyledPrice = styled(Price)`
  margin-left: ${cssTheme.layout.spacing.s800};
  ${cssTheme.typography.bodyM}
`

const StyledDivider = styled(Divider)`
  margin: ${cssTheme.layout.spacing.s600} 0 ${cssTheme.layout.spacing.s500};
`

const Title = () => (
  <Text variant="titleM">
    <FormattedMessageWrapper id="taxesFees" defaultMessage="Taxes and fees" />
  </Text>
)

const TaxSection = ({
  taxValue,
  currencyCode,
  sectionTitle,
  sectionSubtitle
}: TaxSectionProps) => {
  return (
    <Grid
      container
      spacing="s000"
      direction="row"
      wrap="nowrap"
      alignItems="flex-start"
      justify="space-between"
    >
      <Grid container spacing="s000" direction="column">
        <Text variant="bodyM">{sectionTitle}</Text>
        <Text variant="bodyS" colorPath="content.neutral.secondary">
          {sectionSubtitle}
        </Text>
      </Grid>
      <StyledPrice
        maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
        minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
        value={taxValue}
        currency={currencyCode}
        size="md"
        noStyle
      />
    </Grid>
  )
}

type DisclaimerProps = {
  displayCurrencyCode: string
  chargeableCurrencyCode?: string
  propertyCurrencyCode?: string
  hotelTaxes: number
}

const DisclaimerIcon = styled(Text)`
  margin-top: ${cssTheme.layout.spacing.s100};
`

export const Disclaimer = ({
  displayCurrencyCode,
  chargeableCurrencyCode,
  propertyCurrencyCode,
  hotelTaxes
}: DisclaimerProps) => {
  const shouldShowCurrencyConversionDisclaimer =
    displayCurrencyCode !== chargeableCurrencyCode
  const shouldShowLocalTaxesDisclaimer = hotelTaxes > 0

  if (
    !shouldShowCurrencyConversionDisclaimer &&
    !shouldShowLocalTaxesDisclaimer
  ) {
    return null
  }

  return (
    <>
      <StyledDivider />
      <Box display="flex" gap="s200">
        <DisclaimerIcon>
          <Icon
            name="Information"
            colorPath="content.neutral.secondary"
            size="sm"
          />
        </DisclaimerIcon>
        <Box display="flex" flexDirection="column" gap="s300">
          {shouldShowCurrencyConversionDisclaimer && (
            <Text variant="bodyS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper
                id="cancellationPenalties.penaltyConversionDisclaimer"
                defaultMessage="{displayCurrencyCode} price shown is approximate based on the exchange rate of charged currency ({chargeableCurrencyCode}) at the time of booking."
                values={{
                  displayCurrencyCode: displayCurrencyCode,
                  chargeableCurrencyCode: chargeableCurrencyCode
                }}
              />
            </Text>
          )}
          {shouldShowLocalTaxesDisclaimer && (
            <Text variant="bodyS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper
                id="taxesAndFees.localTaxesCurrencyDisclaimer"
                defaultMessage="Local taxes are paid at the property in local currency ({propertyCurrency})."
                values={{
                  propertyCurrency: propertyCurrencyCode
                }}
              />
            </Text>
          )}
        </Box>
      </Box>
    </>
  )
}

const Content = ({
  taxes,
  hotelTaxes,
  currencyCode,
  withoutPaddings
}: ContentProps) => {
  const {brandTaxesDisclaimerMessage} = useBrand()

  return (
    <ContentContainer
      container
      spacing="s000"
      direction="column"
      className={cx(withoutPaddings && '--no-paddings')}
    >
      <div data-id="taxesAndFeesRow">
        <Grid container spacing="s000" direction="column" gap="s600">
          {taxes > 0 && (
            <TaxSection
              taxValue={taxes}
              currencyCode={currencyCode}
              sectionTitle={
                <FormattedMessageWrapper
                  id="taxesFees"
                  defaultMessage="Taxes and fees"
                />
              }
              sectionSubtitle={
                <FormattedMessageWrapper {...brandTaxesDisclaimerMessage} />
              }
            />
          )}

          {hotelTaxes > 0 && (
            <div data-id="localTaxesRow">
              <TaxSection
                taxValue={hotelTaxes}
                currencyCode={currencyCode}
                sectionTitle={
                  <FormattedMessageWrapper
                    id="taxesAndFees.localTaxes"
                    defaultMessage="Local taxes"
                  />
                }
                sectionSubtitle={
                  <FormattedMessageWrapper
                    id="taxesAndFees.localTaxesCaption"
                    defaultMessage="Mandatory local taxes will be collected by the property during your stay."
                  />
                }
              />
            </div>
          )}
        </Grid>
      </div>
      <StyledDivider />
      <div data-id="totalTaxesAndFeesRow">
        <Grid spacing="s000" container direction="row" justify="space-between">
          <Text variant="titleM">
            <FormattedMessageWrapper
              id="taxesAndFees.totalTaxesAndFees"
              defaultMessage="Total taxes and fees"
            />
          </Text>
          <Price
            maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
            minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
            value={taxes + hotelTaxes}
            currency={currencyCode}
            size="md"
          />
        </Grid>
      </div>
    </ContentContainer>
  )
}

export const TaxesAndFees = ({
  isOpen,
  taxes,
  hotelFeesTotal,
  currencyCode,
  onClose
}: Props) => {
  const {isMobile} = useDeviceLayout()
  const hotelTaxes = hotelFeesTotal

  return isMobile ? (
    <BottomSheet
      isOpen={isOpen}
      navBarProps={{centerContent: <Title />}}
      onClose={onClose}
    >
      <Content
        taxes={taxes}
        hotelTaxes={hotelTaxes}
        currencyCode={currencyCode}
        withoutPaddings
      />
    </BottomSheet>
  ) : (
    <PopUp
      isOpen={isOpen}
      navBarProps={{centerContent: <Title />}}
      closeAction={onClose}
      width={520}
    >
      <Content
        taxes={taxes}
        hotelTaxes={hotelTaxes}
        currencyCode={currencyCode}
        withoutPaddings
      />
    </PopUp>
  )
}
